import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { vaccinationAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiShield, FiPlus, FiX, FiAlertTriangle, FiCheckCircle, FiTrendingUp, FiUploadCloud } from 'react-icons/fi';
import toast from 'react-hot-toast';
import CsvUploader from '../components/CsvUploader';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const Vaccinations = () => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { canCreate, canEdit } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [filters, setFilters] = useState({ region: '', vaccineType: '' });
  const [form, setForm] = useState({
    vaccineType: 'COVID-19', region: 'Northeast', category: 'adult',
    demographics: { ageGroup: '25-44' },
    doses: { administered: '', target: '' },
    coverageRate: '', period: { year: 2024, quarter: 'Q4' }
  });

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    try {
      const [recs, st] = await Promise.all([
        vaccinationAPI.getAll(filters),
        vaccinationAPI.getStats()
      ]);
      setRecords(recs.data.records || recs.data || []);
      setStats(st.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await vaccinationAPI.create({
        ...form,
        doses: { administered: Number(form.doses.administered), target: Number(form.doses.target) },
        coverageRate: Number(form.coverageRate)
      });
      toast.success('Vaccination record added');
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to add record'); }
  };

  const byVaccine = stats?.byVaccine || [];
  const byRegion = stats?.byRegion || [];
  const avgCoverage = stats?.avgCoverage || 0;
  const totalDoses = stats?.totalDoses || 0;
  const underVaccinated = stats?.underVaccinated || [];

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Vaccination & Immunization Tracker</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Track vaccination coverage, identify gaps, and manage campaigns</p>
        </div>
        <div className="page-header-actions" style={{ display: 'flex', gap: 10 }}>
          {canCreate && <button onClick={() => setShowCsvUpload(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#f0f9ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiUploadCloud size={18} /> CSV Upload
          </button>}
          {canCreate && <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiPlus size={18} /> Add Record
          </button>}
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { icon: FiShield, label: 'Total Doses Administered', value: totalDoses.toLocaleString(), color: '#3b82f6' },
          { icon: FiCheckCircle, label: 'Avg Coverage Rate', value: `${avgCoverage.toFixed(1)}%`, color: '#10b981' },
          { icon: FiTrendingUp, label: 'Vaccine Types Tracked', value: byVaccine.length, color: '#8b5cf6' },
          { icon: FiAlertTriangle, label: 'Under-vaccinated Groups', value: underVaccinated.length, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{s.label}</p>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{s.value}</p>
              </div>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon size={22} color={s.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Coverage by Vaccine Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byVaccine}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="avgCoverage" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Avg Coverage %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Doses by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={byRegion.map(r => ({ name: r._id, value: r.totalDoses }))} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {byRegion.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar" style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <select value={filters.region} onChange={e => setFilters({ ...filters, region: e.target.value })} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}>
          <option value="">All Regions</option>
          {['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filters.vaccineType} onChange={e => setFilters({ ...filters, vaccineType: e.target.value })} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}>
          <option value="">All Vaccines</option>
          {['COVID-19', 'Influenza', 'MMR', 'Tdap', 'HPV', 'Pneumococcal', 'Hepatitis B'].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {/* Under-vaccinated Alert */}
      {underVaccinated.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#991b1b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertTriangle /> Under-Vaccinated Populations (Coverage {'<'} 50%)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
            {underVaccinated.map((uv, i) => (
              <div key={i} style={{ padding: '10px 16px', background: '#fff', borderRadius: 8, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><strong>{uv._id?.vaccineType}</strong> — {uv._id?.region}</span>
                <span style={{ color: '#ef4444', fontWeight: 700 }}>{uv.avgCoverage?.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Vaccine', 'Region', 'Age Group', 'Doses', 'Target', 'Coverage', 'Period'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r._id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>{r.vaccineType}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.region}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.demographics?.ageGroup || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{(r.doses?.administered || 0).toLocaleString()}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{(r.doses?.target || 0).toLocaleString()}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, maxWidth: 80 }}>
                        <div style={{ height: '100%', background: r.coverageRate >= 70 ? '#10b981' : r.coverageRate >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 3, width: `${Math.min(r.coverageRate, 100)}%` }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: r.coverageRate >= 70 ? '#10b981' : r.coverageRate >= 50 ? '#f59e0b' : '#ef4444' }}>{r.coverageRate?.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{r.period?.year} {r.period?.quarter}</td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No vaccination records found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>Add Vaccination Record</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              {[
                { label: 'Vaccine Type', el: <select value={form.vaccineType} onChange={e => setForm({ ...form, vaccineType: e.target.value })} style={inputStyle}>{['COVID-19', 'Influenza', 'MMR', 'Tdap', 'HPV', 'Pneumococcal', 'Hepatitis B'].map(v => <option key={v}>{v}</option>)}</select> },
                { label: 'Region', el: <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} style={inputStyle}>{['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest'].map(r => <option key={r}>{r}</option>)}</select> },
                { label: 'Age Group', el: <select value={form.demographics.ageGroup} onChange={e => setForm({ ...form, demographics: { ...form.demographics, ageGroup: e.target.value } })} style={inputStyle}>{['0-4', '5-14', '15-24', '25-44', '45-64', '65+'].map(a => <option key={a}>{a}</option>)}</select> },
                { label: 'Doses Administered', el: <input type="number" value={form.doses.administered} onChange={e => setForm({ ...form, doses: { ...form.doses, administered: e.target.value } })} required style={inputStyle} /> },
                { label: 'Target Doses', el: <input type="number" value={form.doses.target} onChange={e => setForm({ ...form, doses: { ...form.doses, target: e.target.value } })} required style={inputStyle} /> },
                { label: 'Coverage Rate (%)', el: <input type="number" step="0.1" value={form.coverageRate} onChange={e => setForm({ ...form, coverageRate: e.target.value })} required style={inputStyle} /> },
              ].map(({ label, el }, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</label>
                  {el}
                </div>
              ))}
              <button type="submit" style={{ width: '100%', padding: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>Add Record</button>
            </form>
          </div>
        </div>
      )}

      {showCsvUpload && (
        <CsvUploader
          title="Vaccination Records"
          templateColumns={['vaccine_type','region','age_group','doses_administered','target_doses','coverage_rate','category','year','quarter']}
          templateSampleRows={[
            ['COVID-19','Northeast','25-44','15000','20000','75','adult','2024','Q4'],
            ['Influenza','Southeast','65+','22000','30000','73','adult','2024','Q4'],
            ['MMR','Midwest','5-14','8000','10000','80','pediatric','2024','Q3']
          ]}
          onUpload={(formData) => vaccinationAPI.bulkImport(formData)}
          onClose={() => { setShowCsvUpload(false); fetchData(); }}
        />
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default Vaccinations;
